import { useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// Shared enlargement viewer for the collection preview grid and the full gallery.
// Keyboard (arrows + Escape), mobile swipe, neighbour preloading, scroll lock.
export function GalleryLightbox({ photos, index, onClose, onNavigate }) {
  const isOpen = index !== null && photos.length > 0;

  const step = useCallback(
    (dir) => {
      if (index === null || photos.length === 0) return;
      onNavigate((index + dir + photos.length) % photos.length);
    },
    [index, photos, onNavigate]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, step]);

  useEffect(() => {
    if (!isOpen || photos.length < 2) return;
    [1, -1].forEach((d) => {
      const n = photos[(index + d + photos.length) % photos.length];
      if (n) {
        const img = new Image();
        img.src = n.image_url;
      }
    });
  }, [isOpen, index, photos]);

  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  if (!isOpen) return null;
  const current = photos[index];

  return (
    <div
      className="fixed inset-0 z-[100] bg-forest/95 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged sale photo"
      data-testid="sale-gallery-lightbox"
    >
      <div className="flex items-center justify-between px-5 py-4">
        <p className="text-ivory/70 text-xs font-bold uppercase tracking-[0.16em]" data-testid="sale-gallery-lightbox-counter">
          {index + 1} / {photos.length}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="w-11 h-11 inline-flex items-center justify-center border border-ivory/30 text-ivory hover:bg-ivory/10 transition-colors"
          aria-label="Close enlarged photo"
          data-testid="sale-gallery-lightbox-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center px-14 sm:px-20 pb-2 min-h-0">
        {photos.length > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 inline-flex items-center justify-center border border-brass/60 text-brass hover:bg-brass hover:text-forest transition-colors"
            aria-label="Previous photo"
            data-testid="sale-gallery-lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <img
          src={current.image_url}
          alt={current.caption || `Estate sale item photo ${index + 1}`}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full w-auto h-auto object-contain border border-ivory/20 shadow-2xl"
          data-testid="sale-gallery-lightbox-image"
        />
        {photos.length > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 inline-flex items-center justify-center border border-brass/60 text-brass hover:bg-brass hover:text-forest transition-colors"
            aria-label="Next photo"
            data-testid="sale-gallery-lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      <div className="px-6 pb-6 pt-2 text-center min-h-[2.5rem]">
        {current.caption && (
          <p className="text-ivory/85 text-sm sm:text-base" data-testid="sale-gallery-lightbox-caption">
            {current.caption}
          </p>
        )}
      </div>
    </div>
  );
}
